import { useEffect, useRef, useState } from "react";
import { Card, Form, Radio, Button } from "antd";
import { useParams, useOutletContext } from "react-router-dom";
import { LocationListForm, LocationAccessControl } from "@seims/shared";

import { openCaptureNotification } from "../../custom/CaptureNotifications"
import { Notify } from "../../../services/custom/Messages";
import { levelsEnum, recordError } from "../../../utils/logService";

import lookupService from "../../../services/lookupService";
import { useOfflineMutationQueue } from "../../../hooks/useOfflineMutationQueue";
import { OFFLINE_INDIVIDUAL_CAPTURE_QUEUE_KEY } from "../../../hooks/offlineQueueConfig";

import {
  PARTICIPANT_TYPES_STORE_NAME,
  PARTICIPANT_DISABILITIES_STORE_NAME,
  GENDERS_STORE_NAME,
  RACES_STORE_NAME,
  AGE_GROUPS_STORE_NAME,
  PROVINCES_DISTRICTS_TOWNS_STORE_NAME,
  APPLICATION_NAMES,
} from "../../../constants";

import '../eventDetails.css'

export const IndividualCapturing = () => {
  const [form] = Form.useForm();
  const { eventId } = useParams();
  const { coords, setOnlineCount } = useOutletContext();

  // Lookups
  const [participantTypes, setParticipantTypes] = useState([]);
  const [disabilityTypes, setDisabilityTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [races, setRaces] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lookupsLoadingRef = useRef(false);

  // Selected values
  const [selected, setSelected] = useState({
    participantType: null,
    disabilityTypes: [],
    gender: null,
    race: null,
    ageGroup: null,
  });

  const { runWithOfflineFallback } = useOfflineMutationQueue({
    storageKey: OFFLINE_INDIVIDUAL_CAPTURE_QUEUE_KEY,
    onFlush: ({ syncedCount, failedCount }) => {
      if (syncedCount > 0) {
        openCaptureNotification(
          "Individual Capture",
          `${syncedCount} queued Individual Capture${syncedCount > 1 ? "s" : ""} synced successfully`,
          Notify.SUCCESS,
        );
      }
      if (failedCount > 0) {
        openCaptureNotification(
          "Individual Capture",
          `${failedCount} queued Individual Capture${failedCount > 1 ? "s" : ""} failed to sync`,
          Notify.ERROR,
        );
      }
    },
  });

  useEffect(() => {
    if (!eventId || lookupsLoadingRef.current) {
      return;
    }

    lookupsLoadingRef.current = true;

    loadLookups().finally(() => {
      lookupsLoadingRef.current = false;
    });
  }, [eventId]);

  const loadLookups = async () => {
    try {
      const results = await Promise.allSettled([
        lookupService.getCahedLookup(PARTICIPANT_TYPES_STORE_NAME),
        lookupService.getCahedLookup(PARTICIPANT_DISABILITIES_STORE_NAME),
        lookupService.getCahedLookup(GENDERS_STORE_NAME),
        lookupService.getCahedLookup(RACES_STORE_NAME),
        lookupService.getCahedLookup(AGE_GROUPS_STORE_NAME),
        lookupService.getCahedLookup(PROVINCES_DISTRICTS_TOWNS_STORE_NAME),
      ]);

      const getLookupData = (result, lookupName) => {
        if (result.status === "fulfilled") {
          return result.value?.data ?? [];
        }

        recordError(result.reason, levelsEnum.ERROR, APPLICATION_NAMES.EVENTS_APP);
        console.warn(`[individual-capturing] Failed to load ${lookupName}`, result.reason);
        return [];
      };

      const [
        participantResp,
        disabilityResp,
        genderResp,
        raceResp,
        ageGroupResp,
        provinceResp,
      ] = results;

      setParticipantTypes(getLookupData(participantResp, PARTICIPANT_TYPES_STORE_NAME));
      setDisabilityTypes(getLookupData(disabilityResp, PARTICIPANT_DISABILITIES_STORE_NAME));
      setGenders(getLookupData(genderResp, GENDERS_STORE_NAME));
      setRaces(getLookupData(raceResp, RACES_STORE_NAME));
      setAgeGroups(getLookupData(ageGroupResp, AGE_GROUPS_STORE_NAME));
      setProvinces(getLookupData(provinceResp, PROVINCES_DISTRICTS_TOWNS_STORE_NAME));
    } catch (err) {
      recordError(err, levelsEnum.ERROR, APPLICATION_NAMES.EVENTS_APP);
    }
  };

  const renderGrid = (items, selectedKey, onSelect, gridTestId, tilePrefix) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        rowGap: 14,
        columnGap:12
      }}
      className="capture-attendee-columns"
      data-testid={gridTestId}
    >
      {items.map((item) => (
        <div
          key={item.key}
          data-testid={`${tilePrefix}-${item.key}`}

          className={`tile ${selectedKey === item.key ? "tile--active" : ""
            }`}

          onClick={() => onSelect(item.key)}
        >
          {item.value}
        </div>
      ))}
    </div>
  );

  const NONE_DISABILITY_KEY = disabilityTypes.find(
    (d) => d.value?.toLowerCase() === "none"
  )?.key;

  const resetCaptureForm = () => {
    form.resetFields();
    setSelected({
      participantType: null,
      gender: null,
      race: null,
      ageGroup: null,
      disabilityTypes: [],
    });
    setShowErrors(false);
  };

  const onFinish = async (values) => {

    if (submitting) return; 
    setSubmitting(true);

    setShowErrors(true);

    const errors = [];

    //  Tile validations
    if (!selected.participantType) errors.push("Attendee Type is required");
    if (!selected.gender) errors.push("Gender is required");
    if (!selected.race) errors.push("Race is required");
    if (!selected.ageGroup) errors.push("Age Group is required");
    if (selected.disabilityTypes.length === 0)
      errors.push("At least one Disability Type is required");

    //  Form validations
    if (!values.province) errors.push("Province is required");
    if (!values.district) errors.push("District is required");

    if (values.isRepeatAttendee === undefined)
      errors.push("Please indicate if this is a repeat attendee");

    if (errors.length > 0) {
      // errors.forEach((msg) => message.error(msg));
      setSubmitting(false)
      return;
    }

    //  Backend payload
    const payload = {
      type: selected.participantType,
      gender: selected.gender,
      race: selected.race,
      ageGroup: selected.ageGroup,
      disabilities: selected.disabilityTypes,

      province: values.province,
      district: values.district,

      repeat: values.isRepeatAttendee,
      recordedDate: new Date().toISOString(),
      // Hard‑coded location for now
      location: {
        x: coords.latitude,
        y: coords.longitude,
      },

    };

    try {

      const { queued, response } = await runWithOfflineFallback({ eventId, payload }, { count: 1 });

      if (queued) {
        openCaptureNotification(
          "Individual Capture",
          "You are offline. Individual Capture was queued and will sync when online",
          Notify.INFO,
        );
        resetCaptureForm();
        return;
      }

      openCaptureNotification(
        "Participant",
        "Individual participant added successfully",
        Notify.SUCCESS
      );

      // Every successful online capture returns total count
      if(response?.data?.total){
        setOnlineCount(response?.data?.total)
      }

      // Clear form to add more
      resetCaptureForm();

    } catch (error) {
      recordError(error, levelsEnum.ERROR, APPLICATION_NAMES.EVENTS_APP);

      openCaptureNotification(
        "Individual Capture",
        "Failed to add individual participant, please contact SEIMS Help Desk",
        Notify.ERROR
      );
    } finally {
      setSubmitting(false);
    }

  };

  return (
    <LocationAccessControl>
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={(errorInfo) => {
        console.log(" onFinishFailed", errorInfo);
        setShowErrors(true);
      }}
    >
      <Card style={{ borderRadius: 0, marginTop: 0 }} variant="borderless" className="capture-attendees" data-testid="individual-capture-form">
        <Card type="inner" title="Attendee Type" className="event-card-inner" style={{ marginBottom: 8 }} data-testid="attendee-type-card">
          {renderGrid(participantTypes, selected.participantType, (key) =>
            setSelected((s) => ({ ...s, participantType: key }))
          , "attendee-type-grid", "attendee-type-tile")}
          {showErrors && !selected.participantType && (
            <div style={{ color: "red", marginTop: 4 }}>
              Attendee Type is required
            </div>
          )}

        </Card>

        {/*  Location Form List */}

        <div className="location-compact">
          <LocationListForm
            form={form}
            provinces={provinces}
          />
        </div>

        {showErrors && !form.getFieldValue("province") && (
          <div style={{ color: "red", marginTop: 4 }} className = "capture-attendee-columns">
            Province is required
          </div>
        )}

        {showErrors && !form.getFieldValue("district") && (
          <div style={{ color: "red", marginTop: 4 }}>
            District is required
          </div>
        )}

        <Card type="inner" className="event-card-inner" title="Disability (Select all that apply)" style={{ marginBottom: 8 }} data-testid="disability-card">
          <div
            className = "capture-attendee-columns"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              rowGap: 14,
              columnGap:12
            }}
            data-testid="disability-grid"
          >
            {disabilityTypes.map((item) => (
              <div
                key={item.key}
                data-testid={`disability-tile-${item.key}`}
                className={`tile ${selected.disabilityTypes.includes(item.key)
                  ? "tile--active"
                  : ""
                  }`}
                onClick={() => {
                  setSelected((s) => {
                    const key = item.key;

                    // If "None" is clicked → clear all others
                    if (key === NONE_DISABILITY_KEY) {
                      return {
                        ...s,
                        disabilityTypes: [NONE_DISABILITY_KEY],
                      };
                    }

                    //  If another option is clicked → remove "None"
                    const withoutNone = s.disabilityTypes.filter(
                      (k) => k !== NONE_DISABILITY_KEY
                    );

                    const next = withoutNone.includes(key)
                      ? withoutNone.filter((k) => k !== key)
                      : [...withoutNone, key];

                    return {
                      ...s,
                      disabilityTypes: next,
                    };
                  });
                }}
              >
                {item.value}
              </div>
            ))}
            {showErrors && selected.disabilityTypes.length === 0 && (
              <div style={{ color: "red", marginTop: 4 }}>
                At least one Disability Type is required
              </div>
            )}
          </div>

        </Card>

        <Card type="inner" className="event-card-inner" title="Gender" style={{ marginBottom: 8 }} data-testid="gender-card">
          {renderGrid(genders, selected.gender, (key) =>
            setSelected((s) => ({ ...s, gender: key }))
          , "gender-grid", "gender-tile")}
          {showErrors && !selected.gender && (
            <div style={{ color: "red", marginTop: 4 }}>
              Gender is required
            </div>
          )}

        </Card>

        <Card type="inner" className="event-card-inner" title="Race" style={{ marginBottom: 8 }} data-testid="race-card">
          {renderGrid(races, selected.race, (key) =>
            setSelected((s) => ({ ...s, race: key }))
          , "race-grid", "race-tile")}
          {showErrors && !selected.race && (
            <div style={{ color: "red", marginTop: 4 }}>
              At least one Race is required
            </div>
          )}
        </Card>

        <Card type="inner" className="event-card-inner" title="Age Group" style={{ marginBottom: 8 }} data-testid="age-group-card">
          {renderGrid(ageGroups, selected.ageGroup, (key) =>
            setSelected((s) => ({ ...s, ageGroup: key }))
          , "age-group-grid", "age-group-tile")}
          {showErrors && !selected.ageGroup && (
            <div style={{ color: "red", marginTop: 4 }}>
              Age group is required
            </div>
          )}

        </Card>

        <Card type="inner" className="event-card-inner repeat-attendee-card" title="Repeat Attendee" data-testid="repeat-attendee-card" style={{ marginBottom: 8 }}>
          <Form.Item
            name="isRepeatAttendee"
            // label={
            //   <span className="event-card-inner" style={{ fontWeight: 600 }}>
            //     Is this a repeat attendee?
            //   </span>
            // }
            rules={[
              { required: true, message: "Please select an option" },
            ]}
          >
            <Radio.Group data-testid="repeat-attendee-group">
              <Radio value={true} data-testid="repeat-attendee-yes">Yes</Radio>
              <Radio value={false} data-testid="repeat-attendee-no">No</Radio>
            </Radio.Group>
          </Form.Item>
        </Card>

        <div className="footer-holder">
          <Button
          className="group-footer"
          type="primary"
          htmlType="submit"
          data-testid="add-individual-submit"
          block
          style={{
            background: "#1F7AB6",
            borderRadius: 20,
            padding: "6px 28px",
            fontWeight: 500,
            border: "none",
          }}
          loading={submitting}
          disabled={submitting}
        >
          + Add Individual
        </Button>
        </div>
        <br/><br/>
      </Card>
    </Form >
    </LocationAccessControl>
  );
};